import { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, selectClasses, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';

import {
  ActionItem,
  LeaseLifecycle,
  Unit,
  UserData,
} from '@urbanmix-tech/shared-js';

import {
  convertPseudoDateToRealDate,
  getLongNumberStr,
} from '@/helpers/common';

import {
  ITableCell,
  ITableRow,
  VirtualizedTable,
  ColumnSeletion
} from '@/components/shared/Table';

// Redux
import { useDispatch } from 'react-redux';
import {
  setHighlightedUnits,
  setSelectedUnit,
  setApartmentData,
  setVisualizer,
  setHoveredUnitId,
  setBuildingVisual,
} from '@/features/project/projectSlice';
import {
  ColorGradeVisualizer,
  ColorGradientVisualizer,
  Visualizer,
} from '@urbanmix-tech/um-embedded';

import { GenericDistinctVisualizer,colorScales } from './BuildingVisualizers'
import type { ColorScaleOptions } from './BuildingVisualizers';

import { parse } from 'csv-parse/sync';
import { ReportsTabContent } from '../UnitTabs/ReportsTabContent';
import { ClassNames } from '@emotion/react';


const steps: Record<string, number> = {
  'waiting_to_send': 1,
  'waiting_for_response': 2,
  'waiting_for_tenant_signature': 3,
  'waiting_for_countersignature': 4,
  'upload_lease': 5,
};
  
let stepsNum: Record<number,string> = {}
  
for(const key in steps) {
    const value = steps[key];
    stepsNum[value] = key;   
}

let stepToStatus : Record<number,string> = {
    1: 'Started',
    2: 'Sent to tenant',  
    3: 'Tenant signed',
    4: 'Completed',
    5: 'Completed',
}

export type CustomReportColumn = {
  label: string;
  sortable: boolean;
  className: string;
  showCheckbox: boolean;
  kind? : string;
}
export type CustomReport = {
  name: string;
  data : string;
  columns? : CustomReportColumn[]
  columnsHeaders? : boolean;
  aggregate? : string[];  
}
  

type CustomReportTabContentProps = {  
  report : CustomReport;
  hoveredUnitId: string | null;
  onSelectUnit(unitId: string | null): void;
  onHoverUnit(unitId: string | null): void;
  colorScale? : ColorScaleOptions
};

export function CustomReportTabContent({
  report,  
  hoveredUnitId,
  onSelectUnit,
  onHoverUnit,
  colorScale = 'default',
}: CustomReportTabContentProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Mount Redux dispatcher
  const dispatch = useDispatch();

  const handleClickRow = (apt: any) => {        
    dispatch(setSelectedUnit({ apartmentId: apt.id }));    
  };

  const [columns,setColumns] = useState<ITableCell[]>([]);

  const [tableData, setTableData] = useState({
    columns: [] as ITableCell[],
    rows: [] as ITableRow[],
  })

  // read csv from report.data
  useEffect(()=> {
    
    let cols : ITableCell[] = []

    const rawRecords = parse(report.data) as string[][];

    const headers = rawRecords[0];

    if (report.columns) {
      // create columns from report.columns
      cols = report.columns.map((col) => {
        return col;
      })
    } else {
      // create columns from first line of csv      
      cols = headers.map((header) => {
        return {
          label: header,
          sortable: true,
          className: 'bg-gray-950',
          showCheckbox: true,
        }
      })                    
    }

    console.log('columns cols',cols)

    const rows : ITableRow[] = rawRecords.slice(1).map((record) => {
      // const newRecord: Record<string, unknown> = {};
      let newRows : ITableRow[] = [] 

      let data : Record<string, ITableCell> = {}
      record.forEach((item, index) => {
        if (index >= cols.length) {
          return;
        }
        const col = cols[index];
        const colKey = col.label as string;    

        let value : any = item;

        if (col.kind === 'currency') {
          value = parseCurrencyString(item) ?? 0
          // console.log('currency',value)
        }
        
        
        data[colKey as string] = {
          label: item,
          value: value,
          className: '',
        } as ITableCell;
        
        })
        return {
          id: record[0],
          data: data,
        }
      });
      // console.log('rows',rows)

    setTableData({
      columns: cols,
      rows: rows,
    });
      
  },[report]);


  
  const [selectedColumn, setSelectedColumn] = useState<ColumnSeletion | null>(null);

  useEffect(() => {
    if (!tableData.rows || tableData.rows.length === 0) {
      return;
    }

    console.log('selectedColumn',selectedColumn)
    if (!selectedColumn || selectedColumn.value === false) {      
      // dispatch(setBuildingVisual({
      //   apartmentData: [],
      //   visualizer: null,
      //   }))
      dispatch(setHighlightedUnits([]));
      return 
    }

    const data = tableData.rows.map((row) => {
      if (!selectedColumn || !selectedColumn.label) {
        return {
          apartmentId: row.id,
          value: 0,
          label: 'Rent'
        };
      }
      // console.log('selectedColumn',selectedColumn)
      // console.log('row',row)
      const value = row.data[selectedColumn?.label as string].value;
      const label = selectedColumn?.label as string;
      // console.log(label,value)
      return {
        apartmentId: row.data['Unit'].value,
        value: value as number,
        label
      };
    })

    console.log('data',data)

    const values = data.map((row) => row.value)
    const distinctValues = new Set(values);
    const colors = colorScales[colorScale];
          
    let visualizer = null;
    // if less than 10 distinct values
    if (distinctValues.size < 10) {
      let colorMap = new Map();
      let arrDistinctValues = Array.from(distinctValues);
      arrDistinctValues.forEach((value, index) => {
        colorMap.set(value,colors[index])
        });
      // console.log('setting visualizer for distinct values', colorMap);
      visualizer = new GenericDistinctVisualizer(colorMap);
    } else {

      const colDef : ITableCell | undefined = tableData.columns.find((col) => col.label === selectedColumn?.label);
      // console.log('colDef',colDef)
        
      if (colDef && (colDef.kind === 'currency' || colDef.kind === 'number')) {
        console.log('setting visualizer for currency values',values);
        const minValue = values.reduce((acc, value) => {
          if (value != 0) {
            return Math.min(acc as number, value as number);
          }
          return acc;
        }, Infinity);
        const maxValue = values.reduce((acc, value) => {
          return Math.max(acc as number, value as number);
        }, -Infinity);
        console.log('setting visualizer for non distinct values',values, minValue,maxValue);
        // visualizer = new ColorGradientVisualizer(['#313695', '#A50026'], minValue, maxValue);
        visualizer = new ColorGradeVisualizer(colors, minValue, maxValue);
      } else {
        visualizer = new ColorGradeVisualizer(colors, '', '');
      }
      
    }          

    // let visualizer = new GenericDistinctVisualizer(new Map([['affordable', colorScales[selectedScale][0]], ['market', colorScales[selectedScale][1]]]));
    dispatch(setBuildingVisual({
      apartmentData: data,
      visualizer: visualizer,
      }))
    
  //    
    setTimeout(() => {
      dispatch(setHighlightedUnits(data));
    }, 500);
  
  },[tableData,colorScale,selectedColumn])


  return (
    <div className="unit-mix h-[calc(100vh-112px)] w-full" ref={ref}>
      <div className="mb-4 relative z-10 top-0 right-0 flex flex-row gap-4">
        {/* <FormControlLabel
          sx={{fontSize: '0.5rem'}}
          control={
        <Checkbox
          size='small'
          checked={showAffordable}
          onChange={(e) => setShowAffordable(e.target.checked as boolean)}
        />
          }
          label={<Typography variant='captionLight'>Affordable</Typography>}
        />
        <FormControlLabel
          control={
        <Checkbox
          checked={showMarket}
          size='small'
          onChange={(e) => setShowMarket(e.target.checked as boolean)}
        />
          }
          label={<Typography variant='captionLight'>Market</Typography>}
        />
        <FormControl size="small">
          <InputLabel postion="left" id="expires-in-days-label"><Typography variant="captionLight">Expires In</Typography></InputLabel>
          <Select
          size='small'
        id="expires-in-days"
        value={expiresInDays}
        label="Expires In"
        onChange={(e) => setExpiresInDays(e.target.value as number)}
          >
        <MenuItem value={30}>
          <Typography variant="captionLight">30 Days</Typography></MenuItem>
        <MenuItem value={60}>
        <Typography variant="captionLight">60 Days</Typography></MenuItem>
        <MenuItem value={90}>
        <Typography variant="captionLight">90 Days</Typography></MenuItem>
          </Select>
        </FormControl> */}       
      </div>
      <VirtualizedTable
        rows={tableData.rows}
        columns={tableData.columns}
        px={8}
        onClickRow={handleClickRow}
        hoveredRowId={hoveredUnitId || undefined}
        onChangeHover={(rowId) => {
          // console.log('on change hover')
          onHoverUnit(rowId)
          dispatch(setHoveredUnitId(rowId));
        }}
        classNames={{
          container:
            'rounded-none border-1 border-b border-b-solid border-zinc-600',
        }}
        onColumnSelected={(column) => {          
          setSelectedColumn(column);
        }}
      />
    </div>
  );
}


function parseCurrencyString(str: string) : number | null {
  // Remove anything that's not digits, minus sign, or decimal point
  const cleanedString = str.replace(/[^0-9.-]+/g, '');
  
  // Parse the cleaned string to float
  const number = parseFloat(cleanedString);

  return isNaN(number) ? null : number;
}